/**
 * The four business types and their optional sub-types (docs/plans/BUSINESS_TYPES_PLAN.md, ADR-0026), and the time zone
 * a sign-up sends without asking. The keys are the server's; the words are in strings.ts.
 */
import { BUSINESS_TYPES, type BusinessType, type Timezone } from './api/endpoints'
import { t } from './strings'

export { BUSINESS_TYPES }

/** The sub-type keys under each type, in the order they are shown. `other` has none, only a free-text note. */
export const SUBTYPES: Record<BusinessType, (keyof typeof t.businessSubtypes)[]> = {
  food: ['restaurant', 'cafe', 'fastfood', 'food_court', 'foodtruck', 'warung', 'drinks', 'bakery', 'catering', 'street_food', 'food_other'],
  services: [
    'barbershop', 'salon_spa', 'skincare', 'massage', 'car_wash', 'workshop', 'laundry', 'shoe_laundry', 'garment', 'repair', 'photo', 'printing',
    'rental', 'rental_ps', 'sports_field', 'isp', 'services_other',
  ],
  retail: [
    'grocery', 'minimarket', 'packaged_food', 'pharmacy', 'clinic', 'cosmetics', 'phone_electronics', 'clothing', 'building', 'petshop', 'florist',
    'online_seller', 'retail_other',
  ],
  other: [],
}

/** The longest note the server accepts. */
export const MAX_TYPE_NOTE = 60

export function businessTypeName(type: unknown): string {
  switch (type) {
    case 'food':
      return t.businessFood
    case 'retail':
      return t.businessRetail
    case 'other':
      return t.businessOther
    default:
      return t.businessServices
  }
}

export function businessTypeHint(type: BusinessType): string {
  return { food: t.businessFoodHint, services: t.businessServicesHint, retail: t.businessRetailHint, other: t.businessOtherHint }[type]
}

const ZONES: readonly Timezone[] = ['Asia/Jakarta', 'Asia/Makassar', 'Asia/Jayapura']

/** WIB for +7, WITA for +8, WIT for +9 (minutes east of UTC: 420, 480, 540); any other offset falls back to Jakarta. */
export function timezoneForOffset(offsetMinutes: number): Timezone {
  if (offsetMinutes === 480) return 'Asia/Makassar'
  if (offsetMinutes === 540) return 'Asia/Jayapura'
  return 'Asia/Jakarta'
}

/**
 * The browser's time zone, as one of the three the server accepts. The IANA name when it is one of them; otherwise the
 * clock's offset from UTC decides. Nothing is asked of the person.
 */
export function detectTimezone(
  resolved: string | undefined = Intl.DateTimeFormat().resolvedOptions().timeZone,
  offsetMinutes: number = -new Date().getTimezoneOffset(),
): Timezone {
  const known = ZONES.find((z) => z === resolved)
  return known ?? timezoneForOffset(offsetMinutes)
}
